/**
 * i18next initialization.
 *
 * Locale resources are bundled statically and **auto-discovered** via Vite's
 * `import.meta.glob`: every `src/i18n/locales/<lang>/<namespace>.json` file is
 * loaded and deep-merged into that language's `translation` bundle. Adding a new
 * namespace file (or a new language folder) requires no change here — just drop
 * the JSON in place. This keeps the architecture modular and scalable.
 *
 * Because resources ship in the bundle, the first paint already has the correct
 * language — no flash of untranslated content, no Suspense boundary needed.
 *
 * Detection order:
 *   1. `?lng=` query string (powers hreflang / shareable locale URLs).
 *   2. Saved choice in localStorage (set when the user picks from the switcher).
 *   3. The browser's preferred language.
 *   4. English fallback.
 *
 * @format
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import {
	DEFAULT_LANGUAGE,
	LANGUAGE_STORAGE_KEY,
	SUPPORTED_LANGUAGE_CODES,
	getLanguageMeta,
} from "./config";

type Dict = Record<string, unknown>;

/** Recursively merge plain objects; arrays and primitives are replaced. */
function deepMerge(target: Dict, source: Dict): Dict {
	for (const key of Object.keys(source)) {
		const sv = source[key];
		const tv = target[key];
		if (
			sv &&
			typeof sv === "object" &&
			!Array.isArray(sv) &&
			tv &&
			typeof tv === "object" &&
			!Array.isArray(tv)
		) {
			deepMerge(tv as Dict, sv as Dict);
		} else {
			target[key] = sv;
		}
	}
	return target;
}

// Eagerly import every locale namespace file: ./locales/<lang>/<namespace>.json
const localeModules = import.meta.glob<{ default: Dict }>(
	"./locales/*/*.json",
	{ eager: true },
);

export const resources: Record<string, { translation: Dict }> = {};

for (const path in localeModules) {
	const match = path.match(/\.\/locales\/([^/]+)\/[^/]+\.json$/);
	if (!match) {
		continue;
	}
	const lng = match[1];
	if (!resources[lng]) {
		resources[lng] = { translation: {} };
	}
	deepMerge(resources[lng].translation, localeModules[path].default);
}

/** Keep <html lang> and <html dir> in sync with the active language (RTL/LTR). */
function applyDocumentLanguage(code: string): void {
	if (typeof document === "undefined") {
		return;
	}
	const meta = getLanguageMeta(code);
	const root = document.documentElement;
	root.setAttribute("lang", meta.code);
	root.setAttribute("dir", meta.dir);
	// Expose the active language as a data attribute so font/RTL CSS can target it.
	root.setAttribute("data-lang", meta.code);

	// Replay the subtle fade so a language switch feels intentional. Removing the
	// class and forcing a reflow lets the same animation run again on each change.
	const body = document.body;
	if (body) {
		body.classList.remove("lang-switching");
		void body.offsetWidth;
		body.classList.add("lang-switching");
	}
}

i18n
	.use(LanguageDetector)
	.use(initReactI18next)
	.init({
		resources,
		fallbackLng: DEFAULT_LANGUAGE,
		supportedLngs: SUPPORTED_LANGUAGE_CODES,
		// Treat "en-US" etc. as "en" so detected locales resolve to a real bundle.
		load: "languageOnly",
		nonExplicitSupportedLngs: true,
		interpolation: {
			// React already escapes output, so i18next must not double-escape.
			escapeValue: false,
		},
		detection: {
			order: ["querystring", "localStorage", "navigator", "htmlTag"],
			lookupQuerystring: "lng",
			lookupLocalStorage: LANGUAGE_STORAGE_KEY,
			caches: ["localStorage"],
		},
		returnNull: false,
	});

// Apply once on boot, then on every change.
applyDocumentLanguage(i18n.language || DEFAULT_LANGUAGE);
i18n.on("languageChanged", applyDocumentLanguage);

export default i18n;
